/**
 * Chat's own side of combined search: message hits through `@wtfalch/chat`
 * 0.4.0's `ChatClient.search` (agora#252), plus two things `search` itself
 * never touches -- room names and company-space people -- matched here
 * straight off `rooms()`/`people()`, both already in memory and synchronous
 * (agora#268).
 *
 * `ChatSearchClient` is a subset of the real `ChatClient`, so the real
 * client satisfies it as-is; nothing here fakes or wraps any of its
 * methods.
 */
import type { ChatClient, ChatTimelineEvent } from '@wtfalch/chat';
import { guestUntilLabel } from '../chat/format.ts';
import type { AgoraRoute } from '../routes.ts';
import type { SearchRow } from './types.ts';

export type ChatSearchClient = Pick<
  ChatClient,
  'search' | 'rooms' | 'people' | 'startDirectMessage'
>;

export type ChatSearchOutcome = {
  rows: readonly SearchRow[];
  /** Rooms this search could not reach -- an encrypted room, most likely,
   *  since Chat carries no crypto yet (`@wtfalch/chat`'s own `client.ts`
   *  doc). Not surfaced by `searchChat` below; a caller that wants to say
   *  so reads it off the raw result before mapping. */
  unsearchableRoomIds: readonly string[];
};

const DEFAULT_LIMIT = 20;

function contains(haystack: string, query: string): boolean {
  return haystack.toLowerCase().includes(query.toLowerCase());
}

function chatRoute(agoraId: string, roomId: string | null): AgoraRoute {
  return {
    kind: 'agora',
    agoraId,
    section: 'chat',
    location: { roomId, threadId: null, eventId: null },
  };
}

function routeFor(agoraId: string, roomId: string, event: ChatTimelineEvent): AgoraRoute {
  // `ChatTimelineEvent` carries no thread id of its own -- a thread's id is
  // its own root message's `id` (`Stream.tsx`'s own `ThreadLink`, whose
  // `onOpen` is `startThread(event.id)` for the very event `.thread` is set
  // on). `.thread` is set only on that root and always `null` on a message
  // read out of the thread's own timeline (`ChatTimelineEvent.thread`'s own
  // doc), so a root's hit opens straight into its thread; anything else --
  // a plain room message, or a reply this shape cannot tell apart from one
  // -- opens the room instead.
  return {
    kind: 'agora',
    agoraId,
    section: 'chat',
    location: { roomId, threadId: event.thread ? event.id : null, eventId: event.id },
  };
}

export async function searchChat(
  client: ChatSearchClient,
  agoraId: string,
  query: string,
  options?: { limit?: number },
): Promise<ChatSearchOutcome> {
  const { results, unsearchableRoomIds } = await client.search(query, {
    limit: options?.limit ?? DEFAULT_LIMIT,
  });
  // A hit is titled by the room it was said in, with the person and the
  // message under it -- the sender alone said nothing about where to look.
  const roomNames = new Map(client.rooms().map((room) => [room.id, room.name]));
  const rows: SearchRow[] = results.map(({ roomId, event }) => ({
    id: event.id,
    title: roomNames.get(roomId) || event.senderName,
    snippet: event.deleted ? event.senderName : `${event.senderName}: ${event.body}`,
    time: new Date(event.ts).toISOString(),
    route: routeFor(agoraId, roomId, event),
  }));
  return { rows, unsearchableRoomIds };
}

/** Non-direct rooms whose name contains `query` -- a channel, not a DM.
 *  Synchronous and local: `rooms()` is already in memory, so this needs no
 *  request of its own and cannot fail the way `searchChat`'s server round
 *  trip can (`model.ts`'s `runSearch` leans on exactly that to keep a
 *  channel findable even when the message search itself is down). */
export function matchRooms(
  client: ChatSearchClient,
  agoraId: string,
  query: string,
): readonly SearchRow[] {
  return client
    .rooms()
    .filter((room) => !room.isDirect && contains(room.name, query))
    .map((room) => ({
      id: room.id,
      title: room.name,
      snippet: room.membership === 'invite' ? 'Invitation' : 'Channel',
      route: chatRoute(agoraId, room.id),
    }));
}

/** `people()` entries whose name or Matrix user id contains `query` --
 *  synchronous and local, the same as `matchRooms`. Keyed by user id, never
 *  merged with `people.ts`'s mail-derived rows: those key by lowercased
 *  email, a different space entirely, and `model.ts` just concatenates the
 *  two lists.
 *
 *  Every row opens through `startDirectMessage`, which reopens the DM with
 *  exactly this person when one exists (invitees count, so a DM started a
 *  second ago is found) and creates it otherwise. `route` points at the
 *  chat section's own root and is never read -- `open` always wins in
 *  `SearchField.tsx`.
 */
export function chatPeopleRows(
  client: ChatSearchClient,
  agoraId: string,
  query: string,
): readonly SearchRow[] {
  return client
    .people()
    .filter((person) => contains(person.name, query) || contains(person.userId, query))
    .map((person) => ({
      id: person.userId,
      title: person.name,
      snippet:
        [
          person.guest ? `Guest · until ${guestUntilLabel(person.guest)}` : undefined,
          person.name === person.userId ? undefined : person.userId,
        ]
          .filter(Boolean)
          .join(' · ') || undefined,
      route: chatRoute(agoraId, null),
      open: async () => chatRoute(agoraId, await client.startDirectMessage([person.userId])),
    }));
}
